import { LampOn, SeparatorHorizontal } from '@/components/icons';
import { DividerCustomizations } from '@/types/report';
import { DividerBlock, TestBlock } from '@/types/report-payload';
import { getTextColorForBackground } from '@/utils/getTextColorForBackground';
import DividerCategoryPicker from '../wizard/DividerCategoryPicker';
import PlaceholderBlock from '../wizard/test-blocks/PlaceholderBlock';
import EditableText from './EditableText';
import type { DividerCategorySelection } from '../wizard/DividerCategoryPicker';
import type { CSSProperties } from 'react';

interface TestDividerProps {
    block: DividerBlock;
    primaryColor: string;
    dividerConfig?: DividerCustomizations;
    onBlockUpdate: (blockId: string | number, updates: Partial<TestBlock>) => void;
    disabled?: boolean;
}

export default function TestDivider({
    block,
    primaryColor,
    dividerConfig,
    onBlockUpdate,
    disabled = false,
}: TestDividerProps) {
    const blockId = block.id || block.order;

    // A category picked here also clears the marker, custom titles included —
    // choosing "Personalizado" is a decision, not a divider still waiting.
    const handleCategorySelect = (selection: DividerCategorySelection | null) => {
        onBlockUpdate(blockId, {
            title: selection?.title ?? '',
            description: selection?.description ?? '',
            awaiting_category: false,
        });
    };

    // Dropped, but not told what it separates yet. The category is chosen from
    // here rather than from the toolbox, so the choice is made looking at the
    // point in the laudo where the new section actually starts.
    if (block.awaiting_category) {
        return (
            <DividerCategoryPicker onSelect={handleCategorySelect} disabled={disabled}>
                <PlaceholderBlock
                    icon={SeparatorHorizontal}
                    label="Clique para escolher a categoria da seção"
                    description={
                        <span className="flex items-center gap-1.5">
                            <LampOn className="size-4 shrink-0 text-yellow-500" />
                            <span>
                                Ex.: Atenção sustentada, Funções executivas — ou um título livre
                            </span>
                        </span>
                    }
                    disabled={disabled}
                />
            </DividerCategoryPicker>
        );
    }

    const alignment = dividerConfig?.alignment || 'center';
    const showLine = dividerConfig?.show_line !== false;
    const fontStyle = dividerConfig?.font_style || 'normal';

    // Resolve background color: 'primary' sentinel maps to the report's primary color
    const rawBgColor = dividerConfig?.background_color ?? 'primary';
    const resolvedBgColor = rawBgColor === 'primary' ? primaryColor : rawBgColor || undefined;

    // Font color: explicit override > auto-calculated from background > default slate
    const textColor = dividerConfig?.font_color
        ? dividerConfig.font_color
        : resolvedBgColor
          ? getTextColorForBackground(resolvedBgColor)
          : undefined;

    const titleStyle: CSSProperties = {
        textAlign: alignment,
        fontWeight: fontStyle === 'bold' ? 'bold' : 'normal',
        fontStyle: fontStyle === 'italic' ? 'italic' : 'normal',
        ...(textColor ? { color: textColor } : {}),
    };

    const containerStyle: CSSProperties = resolvedBgColor
        ? { backgroundColor: resolvedBgColor, padding: '8px 12px', borderRadius: '4px' }
        : {};

    return (
        <div style={containerStyle}>
            <h3
                className={`text-base ${!textColor ? 'text-slate-700' : ''} [&_input]:!text-slate-900`}
                style={titleStyle}
            >
                <EditableText
                    value={block.title}
                    onSave={(value) => onBlockUpdate(blockId, { title: value })}
                    placeholder="Titulo da seção..."
                    className="inline"
                    disabled={disabled}
                />
            </h3>
            {showLine && <hr style={{ borderColor: primaryColor }} className="mt-1 border-t" />}
        </div>
    );
}
